import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import moment from "moment-timezone";
import {
  AGENT_AGREEMENT_PREVIEW_URL,
  AGENT_AGREEMENT_SALESPERSON_FIELD,
  AGENT_AGREEMENT_BROKER_NAME,
  AGENT_AGREEMENT_S3_UPLOAD_HELP,
} from "../../constants/agentAgreement.js";
import {
  getAgentAgreementPageCount,
  signAndUploadAgentAgreement,
} from "../../utils/agentAgreementPdf.js";
import user_service from "../service/user_service.js";

const AgentAgreementSigningModal = ({
  contactId,
  profile,
  paperworkId,
  onSigned,
}) => {
  const signatureCanvasRef = useRef(null);
  const [agentName, setAgentName] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [pageCount, setPageCount] = useState(null);
  const [showSignaturePage, setShowSignaturePage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const defaultName = [profile?.firstName, profile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    setAgentName(defaultName);
  }, [profile?.firstName, profile?.lastName]);

  useEffect(() => {
    const loadPageCount = async () => {
      try {
        const count = await getAgentAgreementPageCount();
        setPageCount(count);
      } catch (loadError) {
        setError(
          loadError.message || AGENT_AGREEMENT_S3_UPLOAD_HELP
        );
      }
    };

    loadPageCount();
  }, []);

  const previewUrl =
    showSignaturePage && pageCount
      ? `${AGENT_AGREEMENT_PREVIEW_URL}#page=${pageCount}&view=FitH&zoom=100`
      : `${AGENT_AGREEMENT_PREVIEW_URL}#view=FitH&zoom=100`;

  const handleSaveSignature = () => {
    if (!signatureCanvasRef.current || signatureCanvasRef.current.isEmpty()) {
      setError("Please draw your signature before saving.");
      return;
    }

    setError("");
    setSignatureDataUrl(signatureCanvasRef.current.toDataURL());
  };

  const handleClearSignature = () => {
    signatureCanvasRef.current?.clear();
    setSignatureDataUrl("");
  };

  const handleSignAgreement = async () => {
    if (!agentName.trim()) {
      setError("Please enter your name as it should appear on the agreement.");
      return;
    }

    if (!signatureDataUrl) {
      setError("Please save your signature before signing the agreement.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await signAndUploadAgentAgreement({
        contactId,
        paperworkId,
        agentName: agentName.trim(),
        fieldValues: {
          [AGENT_AGREEMENT_SALESPERSON_FIELD]: agentName.trim(),
        },
        signatureDataUrl,
      });

      const updatedProfile = await user_service.profileGet(contactId);
      onSigned(updatedProfile?.data || updatedProfile);
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          submitError.message ||
          "Unable to complete signing. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="modal show"
      role="dialog"
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      data-backdrop="static"
      data-keyboard="false"
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable agent-agreement-modal"
        role="document"
        style={{ maxWidth: "1400px", width: "96vw" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">New Broker/Agent Agreement Required</h4>
          </div>
          <div className="modal-body fs-lg">
            <p>
              You must review and electronically sign the updated In Depth Realty
              Broker/Agent Agreement before continuing. Fill in your information
              below, save your signature once, and we will apply your initials and
              signature to the agreement.
            </p>

            <div className="row align-items-stretch">
              <div className="col-md-4">
                <div className="bg-light border rounded-3 p-3">
                  <div className="mb-3">
                    <label className="form-label">Effective Date</label>
                    <input
                      className="form-control"
                      type="text"
                      value={moment().format("M/D/YYYY")}
                      readOnly
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Broker</label>
                    <input
                      className="form-control"
                      type="text"
                      value={AGENT_AGREEMENT_BROKER_NAME}
                      readOnly
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Salesperson Name</label>
                    <input
                      className="form-control"
                      type="text"
                      name={AGENT_AGREEMENT_SALESPERSON_FIELD}
                      value={agentName}
                      onChange={(event) => setAgentName(event.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Signature</label>
                    <div className="canvas_signature w-100">
                      <SignatureCanvas
                        ref={signatureCanvasRef}
                        canvasProps={{
                          className: "signature-canvas w-100",
                          style: { height: 150, border: "1px solid #ccc" },
                        }}
                      />
                      <div className="float-left w-100 mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleClearSignature}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-2"
                          onClick={handleSaveSignature}
                        >
                          Save Signature
                        </button>
                      </div>
                      {signatureDataUrl ? (
                        <p className="text-success small mt-2 mb-0">
                          Signature saved.
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {pageCount ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mb-3"
                      onClick={() => setShowSignaturePage(true)}
                    >
                      Jump to signature page
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={isSubmitting || !signatureDataUrl || !agentName.trim()}
                    onClick={handleSignAgreement}
                  >
                    {isSubmitting ? "Signing..." : "Sign Agreement"}
                  </button>

                  {error ? <p className="text-danger mt-3 mb-0">{error}</p> : null}
                </div>
              </div>

              <div className="col-md-8">
                <div
                  className="agent-agreement-iframe-wrap"
                  style={{
                    height: "calc(100vh - 240px)",
                    minHeight: "1100px",
                  }}
                >
                  <iframe
                    key={previewUrl}
                    title="Broker Agent Agreement"
                    src={previewUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentAgreementSigningModal;
